import { useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  MdDashboard, MdArticle, MdAddCircle, MdCampaign, MdFlag,
  MdBarChart, MdSettings, MdLogout, MdMenu, MdClose, MdTune, MdComment, MdPeople, MdRateReview,
  MdGroups, MdFactCheck, MdAccountBalanceWallet,
  MdHandshake, MdShield, MdInsights, MdSearch, MdExpandMore, MdMoreHoriz, MdAutoAwesome, MdFormatQuote, MdLocationOn
} from 'react-icons/md';

import GlobalSearch from './GlobalSearch';

const navItems = [
  { path: '/', icon: MdDashboard, label: 'Dashboard', end: true },

  { path: '/articles/new', icon: MdAddCircle, label: 'Publish Article', end: true, group: 'Content' },
  { path: '/articles', icon: MdArticle, label: 'Articles', end: true, group: 'Content' },
  { path: '/review-articles', icon: MdRateReview, label: 'Review Articles', end: true, adminOnly: true, group: 'Content' },

  { path: '/comments', icon: MdComment, label: 'Comments', end: false, adminOnly: true, group: 'Moderation' },
  { path: '/moderation', icon: MdFlag, label: 'Moderation', end: false, adminOnly: true, group: 'Moderation' },

  { path: '/status', icon: MdAutoAwesome, label: 'Status Maker', end: false, adminOnly: true },
  { path: '/quotes', icon: MdFormatQuote, label: 'Quotes', end: false, adminOnly: true },

  { path: '/users', icon: MdPeople, label: 'Users', end: false, adminOnly: true, group: 'People' },
  { path: '/admins', icon: MdPeople, label: 'Manage Admins', end: false, adminOnly: true, group: 'People' },

  { path: '/contributors', icon: MdGroups, label: 'Contributors', end: false, adminOnly: true, group: 'Contributors' },
  { path: '/contributor-review', icon: MdFactCheck, label: 'Contributor News', end: false, adminOnly: true, group: 'Contributors' },
  { path: '/contributor-earnings', icon: MdAccountBalanceWallet, label: 'Contributor Earnings', end: false, adminOnly: true, group: 'Contributors' },
  { path: '/withdrawals', icon: MdAccountBalanceWallet, label: 'Withdrawals', end: false, adminOnly: true, group: 'Contributors' },
];

const accordionItems = [
  {
    id: 'others',
    icon: MdMoreHoriz,
    label: 'Others',
    items: [
      { path: '/manage-categories', icon: MdTune, label: 'Manage Categories', end: false, adminOnly: true },
      { path: '/manage-locations', icon: MdLocationOn, label: 'Manage Location Data', end: false, adminOnly: true },
      { path: '/ads', icon: MdCampaign, label: 'Ads Manager', end: false, adminOnly: true },
      { path: '/analytics', icon: MdBarChart, label: 'Analytics', end: false, adminOnly: true },
      { path: '/settings', icon: MdSettings, label: 'Settings', end: false, adminOnly: true },
    ]
  },
  {
    id: 'partners',
    icon: MdHandshake,
    label: 'Partners',
    items: [
      { path: '/partners', icon: MdHandshake, label: 'Partners', end: false, adminOnly: true },
      { path: '/partner-installs', icon: MdShield, label: 'Install Tracking', end: false, adminOnly: true },
      { path: '/partner-payouts', icon: MdAccountBalanceWallet, label: 'Partner Payouts', end: false, adminOnly: true },
      { path: '/partner-analytics', icon: MdInsights, label: 'Partner Analytics', end: false, adminOnly: true },
    ]
  }
];

export default function Layout() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const visibleItems = navItems.filter(item => !item.adminOnly || admin?.role === 'admin');
  const visibleAccordionItems = accordionItems.map(acc => ({
    ...acc,
    items: acc.items.filter(item => !item.adminOnly || admin?.role === 'admin')
  })).filter(acc => acc.items.length > 0);

  // Auto-expand accordion if current route matches any item in it
  const currentRoute = location.pathname;
  const activeAccordionId = visibleAccordionItems.find(acc =>
    acc.items.some(item => item.end ? currentRoute === item.path : currentRoute.startsWith(item.path))
  )?.id;

  // Find the nav item matching the current route, for the topbar title/breadcrumb.
  const allItems = [...visibleItems, ...visibleAccordionItems.flatMap(acc => acc.items)];
  const currentItem = [...allItems].reverse().find(item =>
    item.end ? location.pathname === item.path : location.pathname.startsWith(item.path)
  );
  const pageTitle = currentItem?.label || 'Dashboard';

  let lastGroup = null;

  const toggleAccordion = (id) => {
    setExpandedAccordion(expandedAccordion === id ? null : id);
  };

  return (
    <>
      {/* Mobile header — sits outside .layout so it spans full width */}
      <header className="mobile-header">
        <button className="menu-btn" onClick={() => setSidebarOpen(true)}>
          <MdMenu />
        </button>
        <img src="/startups_logo_transparent.png?v=4" alt="Business Street News" className="admin-logo-sm" />
        <div className="admin-avatar-sm">{admin?.name?.charAt(0)}</div>
      </header>

      {/* Overlay */}
      {sidebarOpen && <div className="overlay" onClick={() => setSidebarOpen(false)} />}

      <div className="layout" data-sidebar-collapsed={sidebarCollapsed}>
        {/* Sidebar */}
        <aside className={`sidebar ${sidebarOpen ? 'open' : ''} ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-header">
            <button className="close-btn" onClick={() => setSidebarOpen(false)}>
              <MdClose />
            </button>
            <div className="logo-container">
              <img src="/startups_logo_white.png?v=5" alt="Business Street News" className="admin-logo" />
            </div>
            <button className="sidebar-toggle" onClick={() => setSidebarCollapsed(!sidebarCollapsed)} title="Toggle sidebar">
              <MdMenu />
            </button>
          </div>

          <nav className="sidebar-nav">
            {visibleItems.map((item) => {
              const showLabel = Boolean(item.group) && item.group !== lastGroup;
              lastGroup = item.group;
              return (
                <div key={item.path}>
                  {showLabel && <div className={`nav-section-label ${sidebarCollapsed ? 'hidden' : ''}`}>{item.group}</div>}
                  <NavLink
                    to={item.path}
                    end={item.end}
                    className={({ isActive }) => `nav-item ${isActive ? 'active' : ''} ${sidebarCollapsed ? 'collapsed' : ''}`}
                    onClick={() => setSidebarOpen(false)}
                    title={sidebarCollapsed ? item.label : ''}
                  >
                    <item.icon className="nav-icon" />
                    <span className={sidebarCollapsed ? 'hidden' : ''}>{item.label}</span>
                  </NavLink>
                </div>
              );
            })}

            {/* Accordion Sections */}
            {visibleAccordionItems.map((accordion) => {
              const isExpanded = expandedAccordion === accordion.id || activeAccordionId === accordion.id;
              return (
                <div key={accordion.id} className="accordion-section">
                  <button
                    className={`accordion-trigger ${isExpanded ? 'expanded' : ''} ${sidebarCollapsed ? 'collapsed' : ''}`}
                    onClick={() => toggleAccordion(accordion.id)}
                    title={sidebarCollapsed ? accordion.label : ''}
                  >
                    <accordion.icon className="nav-icon" />
                    <span className={sidebarCollapsed ? 'hidden' : ''}>{accordion.label}</span>
                    <MdExpandMore className={`accordion-chevron ${isExpanded ? 'expanded' : ''} ${sidebarCollapsed ? 'hidden' : ''}`} />
                  </button>

                  <div className={`accordion-content ${isExpanded ? 'expanded' : ''} ${sidebarCollapsed ? 'hidden' : ''}`}>
                    {accordion.items.map((item) => (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        end={item.end}
                        className={({ isActive }) => `nav-item sub-item ${isActive ? 'active' : ''}`}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <item.icon className="nav-icon" />
                        <span>{item.label}</span>
                      </NavLink>
                    ))}
                  </div>
                </div>
              );
            })}
          </nav>

        </aside>

        {/* Main content */}
        <main className="main-content">
          <header className="topbar">
            <div className="topbar-titles">
              <span className="topbar-title">{pageTitle}</span>
              <span className="topbar-breadcrumb">{admin?.role === 'reporter' ? 'Reporter Panel' : 'Admin Panel'} / {pageTitle}</span>
            </div>

            <GlobalSearch />

            <div className="topbar-actions">
              <Link to="/articles/new" className="topbar-publish-btn">
                <MdAddCircle /> Publish
              </Link>
              <Link to="/settings" className="topbar-profile">
                <div className="admin-avatar">{admin?.name?.charAt(0)}</div>
                <div className="topbar-profile-text">
                  <span className="topbar-profile-name">{admin?.name}</span>
                  <span className="topbar-profile-role">{admin?.role === 'admin' ? 'Administrator' : 'Reporter'}</span>
                </div>
              </Link>
              <button className="topbar-logout-btn" onClick={handleLogout} title="Logout">
                <MdLogout />
              </button>
            </div>
          </header>

          <div className="main-content-body">
            <Outlet />
          </div>
        </main>
      </div>
    </>
  );
}
